import { useMemo, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { GetOrganizationResponse } from '@pillar-portal/api-types';
import { organizationApi } from '@/lib/pillarApi';
import { formatOrganizationDisplayName } from './organizationDisplayName';
import { PolicyPermissionBundlesCard } from './PolicyPermissionBundlesCard';

export function PolicyPermissionBundlesListPage(): ReactElement {
    const params = useParams();
    const organizationUuid: string | null = useMemo(() => {
        const organizationUuidCandidate = params.id;
        if (organizationUuidCandidate === undefined) {
            return null;
        }
        const parseResult = z.string().uuid().safeParse(organizationUuidCandidate);
        return parseResult.success ? parseResult.data : null;
    }, [params.id]);

    const { data: organization, isLoading: isOrganizationLoading } = useQuery<GetOrganizationResponse>({
        queryKey: ['organization', organizationUuid],
        queryFn: async () => {
            if (organizationUuid === null) {
                throw new Error('Invalid organization UUID');
            }
            return await organizationApi.getOrganization(organizationUuid);
        },
        enabled: organizationUuid !== null,
        staleTime: 30000,
    });

    if (isOrganizationLoading) {
        return <div>Loading...</div>;
    }
    if (organizationUuid === null) {
        return <div className="text-destructive">Invalid organization UUID.</div>;
    }
    if (organization === undefined) {
        return <div className="text-destructive">Organization not found.</div>;
    }

    const organizationName: string = formatOrganizationDisplayName(organization);

    return (
        <PolicyPermissionBundlesCard
            organizationUuid={organization.organization_uuid}
            organizationName={organizationName}
            pageTitle={`${organizationName} >> Policies`}
        />
    );
}
